<template>
  <div class="mt-1 mb-6 w-full px-1 md:px-2 space-y-3">
    <!-- Toolbar: count + actions -->
    <div class="flex items-center justify-between gap-3 flex-wrap">
      <p class="text-xs md:text-sm text-gray-600 min-w-0 truncate">
        <template v-if="loadingStudents && students.length === 0">
          Loading {{ labels.STUDENT.toLowerCase() }}s…
        </template>
        <template v-else-if="studentsError">
          Couldn't load {{ labels.STUDENT.toLowerCase() }}s
        </template>
        <template v-else-if="totalRecords > 0">
          <span class="font-semibold text-gray-800">{{ totalRecords }}</span>
          {{ labels.STUDENT.toLowerCase() }}{{ totalRecords === 1 ? '' : 's' }}
          <span class="text-gray-400">·</span>
          <span class="text-gray-500">showing {{ students.length }}</span>
        </template>
        <template v-else-if="hasSearched">
          No {{ labels.STUDENT.toLowerCase() }}s match the current filters
        </template>
        <template v-else>
          Pick a {{ labels.CLASS.toLowerCase() }} or search to load {{ labels.STUDENT.toLowerCase() }}s
        </template>
      </p>
      <div class="flex items-center gap-1">
        <Button
          icon="pi pi-refresh"
          severity="secondary"
          text
          rounded
          size="small"
          :loading="loadingStudents"
          :disabled="!hasSearched"
          @click="refreshList"
          v-tooltip.top="'Refresh'"
          data-testid="btn-student-overview-refresh"
        />
      </div>
    </div>

    <!-- Search and Filter Section -->
    <div id="tour-students-filters"
      class="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-5 gap-3">
        <!-- Search -->
        <div class="sm:col-span-2 xl:col-span-2">
          <label class="block text-[11px] font-medium text-gray-500 mb-1">Search</label>
          <IconField iconPosition="left" class="w-full">
            <InputIcon class="pi pi-search text-gray-400" />
            <InputText v-model="searchQuery" size="small" :placeholder="`${labels.STUDENT} name, ID, email...`"
              class="w-full rounded-lg" @keydown.enter="triggerSearch" />
          </IconField>
        </div>

        <!-- Academic Calendar Filter -->
        <div>
          <label class="block text-[11px] font-medium text-gray-500 mb-1">Academic Calendar</label>
          <AcademicCalendarSelect v-model="selectedAcademicCalendar" size="small" placeholder="Select Session"
            className="w-full rounded-lg" @change="onFilterChange" />
        </div>

        <!-- Classroom Filter -->
        <div>
          <label class="block text-[11px] font-medium text-gray-500 mb-1">{{ labels.CLASS }}</label>
          <Select v-model="selectedClassroom" :options="!isTertiary ? classroomSectionOptions : levelOptions"
            optionLabel="label" optionValue="value" :placeholder="`All ${labels.CLASS}s`" size="small"
            class="w-full rounded-lg" showClear filter @change="onFilterChange"
            data-testid="filter-classroom-section" />
        </div>

        <!-- Status Filter -->
        <div>
          <label class="block text-[11px] font-medium text-gray-500 mb-1">Status</label>
          <EnrollmentStatusSelect v-model="selectedStatus" size="small" @change="onFilterChange" />
        </div>

        <!-- Account Status Filter -->
        <div>
          <label class="block text-[11px] font-medium text-gray-500 mb-1">Account</label>
          <Select v-model="selectedAccountStatus" :options="accountStatusOptions" optionLabel="label" optionValue="value"
            placeholder="All Accounts" size="small" class="w-full rounded-lg" showClear
            @change="onFilterChange" />
        </div>

        <!-- Tertiary Filters -->
        <template v-if="isTertiary">
          <div>
            <label class="block text-[11px] font-medium text-gray-500 mb-1">Programme</label>
            <Select v-model="selectedProgramme" :options="programmeOptions" optionLabel="label" optionValue="id"
              placeholder="Select Programme" size="small" class="w-full rounded-lg" showClear filter
              @change="onFilterChange" />
          </div>
          <div v-if="campuses.length > 0">
            <label class="block text-[11px] font-medium text-gray-500 mb-1">Campus</label>
            <Select v-model="selectedCampus" :options="campusOptions" optionLabel="name" optionValue="id"
              placeholder="Select Campus" size="small" class="w-full rounded-lg" showClear filter
              @change="onFilterChange" />
          </div>
        </template>
    </div>

    <!-- Active Filter Chips -->
    <div v-if="activeFilterChips.length > 0" class="flex flex-wrap items-center gap-2">
      <span class="text-xs font-medium text-gray-500 mr-0.5">Active filters:</span>
      <Chip
        v-for="chip in activeFilterChips"
        :key="chip.key"
        :label="chip.label"
        removable
        class="!bg-primary/10 !text-primary !text-xs !font-medium !py-1"
        @remove="chip.clear()"
      />
      <button
        type="button"
        class="text-xs font-medium text-gray-500 hover:text-red-500 hover:underline underline-offset-2 ml-1"
        @click="clearAllFilters"
      >
        Clear all
      </button>
    </div>

    <!-- Bulk Action Toolbar -->
    <Transition name="slide-down">
      <div v-if="selectedStudents.length > 0"
        class="flex items-center gap-2 flex-wrap px-3 py-2 bg-primary/10 border border-primary/20 rounded-lg">
        <span class="text-xs font-semibold text-primary-700">
          {{ selectedStudents.length }} {{ labels.STUDENT.toLowerCase() }}{{ selectedStudents.length > 1 ? 's' : '' }} selected
        </span>
        <div class="flex-1"></div>
        <Button
          v-if="isTertiary"
          label="Update Placement"
          icon="pi pi-pencil"
          size="small"
          outlined
          @click="showBulkUpdateDialog = true"
          data-testid="btn-bulk-update"
        />
        <Button
          label="Change Status"
          icon="pi pi-sync"
          size="small"
          severity="warn"
          outlined
          @click="showStatusDialog = true"
          data-testid="btn-bulk-status"
        />
        <Button
          label="Disable"
          icon="pi pi-ban"
          size="small"
          severity="danger"
          outlined
          @click="showDisableDialog = true"
          data-testid="btn-bulk-disable"
        />
        <Button
          label="Enable"
          icon="pi pi-check-circle"
          size="small"
          severity="success"
          outlined
          @click="showEnableDialog = true"
          data-testid="btn-bulk-enable"
        />
        <Button
          label="Enroll in Calendar"
          icon="pi pi-calendar-plus"
          size="small"
          severity="help"
          outlined
          @click="showEnrollDialog = true"
          data-testid="btn-bulk-enroll"
        />
        <Button
          label="De-enroll"
          icon="pi pi-calendar-minus"
          size="small"
          severity="danger"
          outlined
          @click="showDeenrollDialog = true"
          data-testid="btn-bulk-deenroll"
        />
        <Button
          v-if="can(userStore.currentUser, AppPermissions.Students.Actions.Update, AppPermissions.Students.Resource)"
          label="Reset Passwords"
          icon="pi pi-key"
          size="small"
          severity="warn"
          outlined
          @click="openResetPasswordsDialog"
          data-testid="btn-bulk-reset-passwords"
        />
        <Button
          icon="pi pi-times"
          text
          rounded
          size="small"
          severity="secondary"
          @click="selectedStudents = []"
          v-tooltip.top="'Clear selection'"
        />
      </div>
    </Transition>

    <!-- Initial Empty State (no search yet) -->
    <div v-if="!hasSearched && !loadingStudents && students.length === 0"
      class="text-center p-6 md:p-8 text-gray-500 bg-white rounded-xl border border-gray-100 shadow-sm">
      <div class="bg-primary/10 w-12 h-12 rounded-full flex items-center justify-center mx-auto mb-3">
        <i class="pi pi-users text-lg text-primary"></i>
      </div>
      <h3 class="text-sm md:text-base font-medium text-gray-900 mb-1">Ready to find {{ labels.STUDENT.toLowerCase() }}s</h3>
      <p class="text-xs text-gray-500 max-w-sm mx-auto">
        Select a {{ labels.CLASS.toLowerCase() }}, programme or status, or search by name or ID above to load {{ labels.STUDENT.toLowerCase() }}s.
      </p>
    </div>

    <!-- The request failed: say so instead of "no students match" -->
    <LoadErrorMessage v-else-if="studentsError && !loadingStudents" :what="`the ${labels.STUDENT.toLowerCase()} list`"
      :error="studentsError" @retry="refetchStudents()" />

    <!-- Data Table -->
    <div v-else id="tour-students-table" class="bg-white rounded-xl shadow-sm border border-gray-100 overflow-hidden">
      <DataTable :value="filteredStudents" :lazy="true" :paginator="students.length > 0" :rows="rows"
        :totalRecords="totalRecords" :loading="loadingStudents" :rowsPerPageOptions="[10, 20, 50, 100, 200]"
        @page="onPageChange"
        v-model:selection="selectedStudents"
        dataKey="id"
        size="small"
        tableStyle="min-width: 48rem"
        class="modern-table"
        paginatorTemplate="FirstPageLink PrevPageLink PageLinks NextPageLink LastPageLink CurrentPageReport RowsPerPageDropdown"
        currentPageReportTemplate="{first}–{last} of {totalRecords}"
      >
        <Column selectionMode="multiple" headerStyle="width: 3rem" />

        <Column field="matric_no" :header="`${labels.STUDENT} ID`" class="font-medium text-gray-700">
          <template #body="slotProps">
            <span class="text-sm font-medium text-gray-700">{{ slotProps.data.matric_no || '—' }}</span>
          </template>
        </Column>

        <Column :header="`${labels.STUDENT} Name`" style="min-width: 16rem">
          <template #body="slotProps">
            <div class="flex items-center gap-3 min-w-0">
              <Avatar
                :label="getStudentInitials(slotProps.data)"
                shape="circle"
                class="shrink-0 !text-white !text-[10px] !font-semibold !w-7 !h-7"
                :class="getAvatarColorClass(slotProps.data)"
              />
              <div class="flex flex-col min-w-0">
                <div class="flex items-center gap-1.5">
                  <span class="font-semibold text-gray-800 truncate">
                    {{ slotProps.data.surname }} {{ slotProps.data.first_name }}
                    <span v-if="slotProps.data.other_name" class="font-normal text-gray-500">{{ slotProps.data.other_name }}</span>
                  </span>
                  <Tag v-if="slotProps.data.is_active === false" value="Disabled" severity="danger" rounded class="!text-[9px] !px-1.5 !py-0.5" />
                </div>
                <span v-if="slotProps.data.email" class="text-xs text-gray-500 truncate">{{ slotProps.data.email }}</span>
                <span v-else class="text-xs text-gray-400 italic">No email</span>
              </div>
            </div>
          </template>
        </Column>

        <Column field="gender" header="Gender">
            <template #body="slotProps">
                <span class="px-2 py-1 text-xs font-semibold rounded-full"
                    :class="slotProps.data.gender?.toLowerCase() === 'female' ? 'bg-pink-50 text-pink-600' : 'bg-primary/10 text-primary'">
                    {{ slotProps.data.gender }}
                </span>
            </template>
        </Column>

        <Column field="className" :header="labels.CLASS">
          <template #body="slotProps">
            <span class="text-sm text-gray-700">
                {{ slotProps.data.currentClassroomSection?.classroom?.name || 'N/A' }}
                <span v-if="slotProps.data.currentClassroomSection?.section?.name" class="text-gray-500">
                - {{ slotProps.data.currentClassroomSection?.section?.name }}
                </span>
            </span>
          </template>
        </Column>

        <Column header="Status">
          <template #body="slotProps">
            <Tag
              v-if="slotProps.data.studentAcademicCalendar?.status"
              :value="enrollmentStatusLabel(slotProps.data.studentAcademicCalendar?.status)"
              :severity="enrollmentStatusSeverity(slotProps.data.studentAcademicCalendar?.status)"
              rounded
              data-testid="student-status-tag"
            />
            <span v-else class="text-xs text-gray-400 italic">N/A</span>
          </template>
        </Column>

        <Column v-if="isTertiary" header="Programme">
          <template #body="slotProps">
            <div class="text-sm text-gray-700 max-w-[200px] truncate" :title="slotProps.data.studentAcademicCalendar?.programme?.program_name">
                {{ slotProps.data.studentAcademicCalendar?.programme?.program_name || 'N/A' }}
            </div>
          </template>
        </Column>

        <Column header="Actions" :exportable="false" style="min-width: 7rem">
          <template #body="slotProps">
            <div class="flex items-center gap-1">
              <Button icon="pi pi-eye" rounded text severity="secondary" size="small"
                v-if="can(userStore.currentUser, AppPermissions.Students.Actions.View, AppPermissions.Students.Resource)"
                @click="viewStudent(slotProps.data.id)"
                v-tooltip.top="`View`" class="!w-7 !h-7" data-testid="btn-student-view" />

              <Button icon="pi pi-pencil" rounded text severity="info" size="small"
                v-if="can(userStore.currentUser, AppPermissions.Students.Actions.Update, AppPermissions.Students.Resource)"
                @click="openEditDialog(slotProps.data)"
                v-tooltip.top="`Edit`" class="!w-7 !h-7" data-testid="btn-student-edit" />

              <Button icon="pi pi-trash" rounded text severity="danger" size="small"
                v-if="can(userStore.currentUser, AppPermissions.Students.Actions.Delete, AppPermissions.Students.Resource)"
                @click="confirmDelete(slotProps.data.id)"
                v-tooltip.top="`Delete`" class="!w-7 !h-7" data-testid="btn-student-delete" />
            </div>
          </template>
        </Column>

        <template #empty>
          <div class="text-center p-10">
            <div class="bg-gray-50 w-14 h-14 rounded-full flex items-center justify-center mx-auto mb-3">
              <i class="pi pi-search text-xl text-gray-400"></i>
            </div>
            <p class="text-sm font-medium text-gray-700 mb-1">No {{ labels.STUDENT.toLowerCase() }}s match</p>
            <p class="text-xs text-gray-500 mb-3">Try removing a filter or broadening your search.</p>
            <Button
              v-if="activeFilterChips.length > 0"
              label="Clear filters"
              icon="pi pi-filter-slash"
              severity="secondary"
              size="small"
              text
              @click="clearAllFilters"
            />
          </div>
        </template>
      </DataTable>
    </div>

    <!-- Delete Confirmation Dialog -->
    <Dialog v-model:visible="showDeleteDialog" header="Confirm Deletion" modal :closable="false" class="w-full max-w-md">
      <div class="text-center p-4">
        <i class="pi pi-exclamation-triangle text-yellow-500 text-4xl mb-4"></i>
        <p class="text-gray-700 text-lg">Are you sure you want to delete this student?</p>
        <p class="text-gray-500 text-sm mt-2">This action cannot be undone.</p>
      </div>
      <div class="flex justify-end gap-3 mt-6 px-4 pb-2">
        <Button label="Cancel" icon="pi pi-times" text @click="showDeleteDialog = false" class="p-button-secondary" data-testid="btn-student-delete-cancel" />
        <Button :label="deleting ? 'Deleting...' : 'Yes, Delete'" icon="pi pi-trash" severity="danger"
          :loading="deleting" @click="deleteStudent" data-testid="btn-student-delete-confirm" />
      </div>
    </Dialog>

    <!-- Edit Dialog -->
    <Dialog v-model:visible="showEditDialog" :header="`Edit ${labels.STUDENT} Details`" modal class="w-full md:w-9/12 lg:w-8/12"
        :pt="{ root: { class: 'rounded-xl shadow-2xl overflow-hidden' }, header: { class: 'bg-gray-50 border-b' } }">
      <StudentDetailsForm v-if="showEditDialog" :initialData="editingStudent" :isEditMode="true"
        @saved="showEditDialog = false" />
    </Dialog>

    <!-- Disable Students Dialog -->
    <Dialog v-model:visible="showDisableDialog" header="Disable Students" modal class="w-full max-w-lg"
        :pt="{ root: { class: 'rounded-xl shadow-2xl overflow-hidden' }, header: { class: 'bg-gray-50 border-b' } }">
      <div class="p-4 space-y-4">
        <!-- Nothing actionable: every selected student is already disabled -->
        <div v-if="toDisable.length === 0" class="flex items-start gap-3 p-3 bg-amber-50 rounded-lg border border-amber-100"
          data-testid="disable-none-actionable">
          <i class="pi pi-info-circle text-amber-500 text-lg mt-0.5"></i>
          <div>
            <p class="text-sm font-medium text-amber-800">
              <template v-if="selectedStudents.length === 1">
                {{ studentDisplayName(alreadyDisabled[0]) }} is already disabled.
              </template>
              <template v-else>
                All {{ selectedStudents.length }} selected {{ labels.STUDENT.toLowerCase() }}s are already disabled.
              </template>
            </p>
            <p class="text-xs text-amber-600 mt-1">Nothing to do here.</p>
          </div>
        </div>

        <template v-else>
          <div class="flex items-start gap-3 p-3 bg-red-50 rounded-lg border border-red-100">
            <i class="pi pi-exclamation-triangle text-red-500 text-lg mt-0.5"></i>
            <div>
              <p class="text-sm font-medium text-red-800">
                You are about to disable {{ toDisable.length }} {{ labels.STUDENT.toLowerCase() }}{{ toDisable.length > 1 ? 's' : '' }}.
              </p>
              <p class="text-xs text-red-600 mt-1">Disabled students will be blocked from accessing their dashboard.</p>
            </div>
          </div>

          <!-- Some of the selection is already disabled -->
          <div v-if="alreadyDisabled.length > 0" class="flex items-start gap-2 p-2.5 bg-amber-50 rounded-lg border border-amber-100"
            data-testid="disable-some-skipped">
            <i class="pi pi-info-circle text-amber-500 text-sm mt-0.5"></i>
            <p class="text-xs text-amber-700">
              <template v-if="alreadyDisabled.length === 1">
                {{ studentDisplayName(alreadyDisabled[0]) }} is already disabled and will be skipped.
              </template>
              <template v-else>
                {{ alreadyDisabled.length }} selected {{ labels.STUDENT.toLowerCase() }}s are already disabled and will be skipped.
              </template>
            </p>
          </div>

          <div>
            <label class="block text-sm font-medium text-gray-700 mb-1">Reason for disabling <span class="text-red-500">*</span></label>
            <Textarea v-model="disableReason" rows="3" class="w-full" placeholder="e.g. Outstanding fees, disciplinary action..." data-testid="input-disable-reason" />
            <small v-if="disableReason.length > 0 && disableReason.length < 5" class="text-red-500">Reason must be at least 5 characters.</small>
          </div>
        </template>
      </div>
      <div class="flex justify-end gap-3 px-4 pb-4">
        <Button :label="toDisable.length === 0 ? 'Close' : 'Cancel'" text severity="secondary" @click="showDisableDialog = false" />
        <Button
          v-if="toDisable.length > 0"
          label="Disable Students"
          icon="pi pi-ban"
          severity="danger"
          :loading="disabling"
          :disabled="disableReason.length < 5"
          @click="handleDisable"
          data-testid="btn-confirm-disable"
        />
      </div>
    </Dialog>

    <!-- Enable Students Dialog -->
    <Dialog v-model:visible="showEnableDialog" header="Enable Students" modal class="w-full max-w-lg"
        :pt="{ root: { class: 'rounded-xl shadow-2xl overflow-hidden' }, header: { class: 'bg-gray-50 border-b' } }">
      <div class="p-4 space-y-4">
        <!-- Nothing actionable: every selected student is already enabled -->
        <div v-if="toEnable.length === 0" class="flex items-start gap-3 p-3 bg-amber-50 rounded-lg border border-amber-100"
          data-testid="enable-none-actionable">
          <i class="pi pi-info-circle text-amber-500 text-lg mt-0.5"></i>
          <div>
            <p class="text-sm font-medium text-amber-800">
              <template v-if="selectedStudents.length === 1">
                {{ studentDisplayName(alreadyEnabled[0]) }} is already enabled.
              </template>
              <template v-else>
                All {{ selectedStudents.length }} selected {{ labels.STUDENT.toLowerCase() }}s are already enabled.
              </template>
            </p>
            <p class="text-xs text-amber-600 mt-1">Nothing to do here.</p>
          </div>
        </div>

        <template v-else>
          <div class="flex items-start gap-3 p-3 bg-green-50 rounded-lg border border-green-100">
            <i class="pi pi-check-circle text-green-500 text-lg mt-0.5"></i>
            <div>
              <p class="text-sm font-medium text-green-800">
                You are about to re-enable {{ toEnable.length }} {{ labels.STUDENT.toLowerCase() }}{{ toEnable.length > 1 ? 's' : '' }}.
              </p>
              <p class="text-xs text-green-600 mt-1">They will regain access to their dashboard.</p>
            </div>
          </div>

          <!-- Some of the selection is already enabled -->
          <div v-if="alreadyEnabled.length > 0" class="flex items-start gap-2 p-2.5 bg-amber-50 rounded-lg border border-amber-100"
            data-testid="enable-some-skipped">
            <i class="pi pi-info-circle text-amber-500 text-sm mt-0.5"></i>
            <p class="text-xs text-amber-700">
              <template v-if="alreadyEnabled.length === 1">
                {{ studentDisplayName(alreadyEnabled[0]) }} is already enabled and will be skipped.
              </template>
              <template v-else>
                {{ alreadyEnabled.length }} selected {{ labels.STUDENT.toLowerCase() }}s are already enabled and will be skipped.
              </template>
            </p>
          </div>

          <div>
            <label class="block text-sm font-medium text-gray-700 mb-1">Reason for enabling <span class="text-red-500">*</span></label>
            <Textarea v-model="enableReason" rows="3" class="w-full" placeholder="e.g. Fees cleared, probation ended..." data-testid="input-enable-reason" />
            <small v-if="enableReason.length > 0 && enableReason.length < 5" class="text-red-500">Reason must be at least 5 characters.</small>
          </div>
        </template>
      </div>
      <div class="flex justify-end gap-3 px-4 pb-4">
        <Button :label="toEnable.length === 0 ? 'Close' : 'Cancel'" text severity="secondary" @click="showEnableDialog = false" />
        <Button
          v-if="toEnable.length > 0"
          label="Enable Students"
          icon="pi pi-check-circle"
          severity="success"
          :loading="enabling"
          :disabled="enableReason.length < 5"
          @click="handleEnable"
          data-testid="btn-confirm-enable"
        />
      </div>
    </Dialog>

    <!-- Update Status Dialog -->
    <Dialog v-model:visible="showStatusDialog" header="Change Enrollment Status" modal class="w-full max-w-md"
        :pt="{ root: { class: 'rounded-xl shadow-2xl overflow-hidden' }, header: { class: 'bg-gray-50 border-b' } }">
      <div class="p-4 space-y-4">
        <p class="text-sm text-gray-600">
          Update enrollment status for <strong>{{ selectedStudents.length }}</strong>
          {{ labels.STUDENT.toLowerCase() }}{{ selectedStudents.length > 1 ? 's' : '' }}.
        </p>
        <div>
          <label class="block text-sm font-medium text-gray-700 mb-1">New Status <span class="text-red-500">*</span></label>
          <EnrollmentStatusSelect v-model="newStatus" placeholder="Select new status" data-testid="select-new-status" />
        </div>
        <div v-if="!selectedAcademicCalendar">
          <label class="block text-sm font-medium text-gray-700 mb-1">Academic Calendar <span class="text-red-500">*</span></label>
          <AcademicCalendarSelect v-model="statusCalendarId" placeholder="Select calendar" className="w-full rounded-lg" />
          <small class="text-gray-500">Required when no calendar is selected in filters.</small>
        </div>
      </div>
      <div class="flex justify-end gap-3 px-4 pb-4">
        <Button label="Cancel" text severity="secondary" @click="showStatusDialog = false" />
        <Button
          label="Update Status"
          icon="pi pi-sync"
          severity="warn"
          :loading="updatingStatus"
          :disabled="!newStatus || (!selectedAcademicCalendar && !statusCalendarId)"
          @click="handleStatusUpdate"
          data-testid="btn-confirm-status"
        />
      </div>
    </Dialog>

    <!-- Bulk Update (Placement) Dialog — Tertiary only -->
    <Dialog v-model:visible="showBulkUpdateDialog" header="Update Student Placement" modal class="w-full max-w-lg"
        :pt="{ root: { class: 'rounded-xl shadow-2xl overflow-hidden' }, header: { class: 'bg-gray-50 border-b' } }">
      <div class="p-4 space-y-4">
        <p class="text-sm text-gray-600">
          Update placement for <strong>{{ selectedStudents.length }}</strong>
          {{ labels.STUDENT.toLowerCase() }}{{ selectedStudents.length > 1 ? 's' : '' }}.
          Only filled fields will be updated.
        </p>
        <div v-if="!selectedAcademicCalendar">
          <label class="block text-sm font-medium text-gray-700 mb-1">Academic Calendar <span class="text-red-500">*</span></label>
          <AcademicCalendarSelect v-model="bulkCalendarId" placeholder="Select calendar" className="w-full rounded-lg" />
        </div>
        <div>
          <label class="block text-sm font-medium text-gray-700 mb-1">Programme</label>
          <Select v-model="bulkProgrammeId" :options="programmeOptions" optionLabel="label" optionValue="id"
            placeholder="No change" class="w-full rounded-lg" showClear filter />
        </div>
        <div>
          <label class="block text-sm font-medium text-gray-700 mb-1">{{ labels.CLASS }}</label>
          <Select v-model="bulkClassroomSectionId" :options="isTertiary ? levelOptions : classroomSectionOptions"
            optionLabel="label" optionValue="value" placeholder="No change" class="w-full rounded-lg" showClear filter />
        </div>
        <div v-if="campuses.length > 0">
          <label class="block text-sm font-medium text-gray-700 mb-1">Campus</label>
          <Select v-model="bulkCampusId" :options="campusOptions" optionLabel="name" optionValue="id"
            placeholder="No change" class="w-full rounded-lg" showClear filter />
        </div>
        <div class="grid grid-cols-2 gap-3">
          <div>
            <label class="block text-sm font-medium text-gray-700 mb-1">Min Units</label>
            <InputNumber v-model="bulkMinUnits" :min="0" placeholder="No change" class="w-full" :useGrouping="false" />
          </div>
          <div>
            <label class="block text-sm font-medium text-gray-700 mb-1">Max Units</label>
            <InputNumber v-model="bulkMaxUnits" :min="0" placeholder="No change" class="w-full" :useGrouping="false" />
          </div>
        </div>
        <small v-if="bulkMinUnits !== null && bulkMaxUnits !== null && bulkMinUnits > bulkMaxUnits" class="text-red-500">
          Min units cannot be greater than max units.
        </small>
        <div class="grid grid-cols-2 gap-3">
          <div>
            <label class="block text-sm font-medium text-gray-700 mb-1">Entry Level</label>
            <InputText v-model="bulkEntryLevel" placeholder="e.g. 200" class="w-full" />
          </div>
          <div>
            <label class="block text-sm font-medium text-gray-700 mb-1">Min Graduation Units</label>
            <InputNumber v-model="bulkMinGraduationUnits" :min="0" placeholder="No change" class="w-full" :useGrouping="false" />
          </div>
        </div>

        <div class="border-t pt-3 mt-2">
          <label class="block text-sm font-medium text-gray-700 mb-2">Update Scope</label>
          <SelectButton v-model="bulkUpdateScope" :options="updateScopeOptions" optionLabel="label" optionValue="value" :allowEmpty="false" class="w-full" />
          <small class="text-gray-500 mt-1 block">
            <span v-if="bulkUpdateScope === StudentUpdateScope.CurrentOnly">Only the selected academic calendar will be updated.</span>
            <span v-else>All academic calendars for the selected student(s) will be updated.</span>
          </small>
        </div>

        <div v-if="bulkProgrammeId || bulkClassroomSectionId" class="flex items-center gap-2 pt-1">
          <Checkbox v-model="bulkCascade" :binary="true" inputId="cascadeCheck" />
          <label for="cascadeCheck" class="text-sm text-gray-700 cursor-pointer">
            Also update course registrations (programme &amp; level)
          </label>
        </div>
      </div>
      <div class="flex justify-end gap-3 px-4 pb-4">
        <Button label="Cancel" text severity="secondary" @click="showBulkUpdateDialog = false" />
        <Button
          label="Update Placement"
          icon="pi pi-check"
          :loading="bulkUpdating"
          :disabled="!bulkHasChanges"
          @click="handleBulkUpdate"
          data-testid="btn-confirm-bulk-update"
        />
      </div>
    </Dialog>

    <!-- De-enroll Dialog -->
    <Dialog v-model:visible="showDeenrollDialog" header="De-enroll Students from Calendar" modal class="w-full max-w-md">
      <div class="space-y-4 pt-2">
        <p class="text-sm text-gray-600">
          Remove <strong>{{ selectedStudents.length }}</strong> selected student(s) from a specific academic calendar.
          This deletes the enrollment record entirely.
        </p>
        <div class="flex flex-col gap-1">
          <label class="text-xs font-medium text-gray-600">Academic Calendar *</label>
          <AcademicCalendarSelect v-model="deenrollCalendarId" size="small" placeholder="Select calendar" className="w-full" />
        </div>
        <div class="bg-amber-50 border border-amber-200 rounded-lg px-3 py-2 text-xs text-amber-700">
          <i class="pi pi-exclamation-triangle mr-1"></i>
          This action cannot be undone. Use "Change Status" instead if you want to keep the enrollment record.
        </div>
      </div>
      <template #footer>
        <Button label="Cancel" text severity="secondary" @click="showDeenrollDialog = false" />
        <Button label="De-enroll" icon="pi pi-calendar-minus" severity="danger"
          :loading="deenrolling" :disabled="!deenrollCalendarId"
          @click="handleDeenroll" />
      </template>
    </Dialog>

    <!-- Enroll in Calendar Dialog -->
    <Dialog v-model:visible="showEnrollDialog" header="Enroll Students in Calendar" modal class="w-full max-w-md">
      <div class="space-y-4 pt-2">
        <p class="text-sm text-gray-600">
          Enroll <strong>{{ selectedStudents.length }}</strong> selected student(s) into a specific academic calendar.
          Students already enrolled in the target calendar will be skipped.
        </p>
        <div class="flex flex-col gap-1">
          <label class="text-xs font-medium text-gray-600">Academic Calendar *</label>
          <AcademicCalendarSelect v-model="enrollCalendarId" size="small" placeholder="Select calendar" className="w-full" />
        </div>
        <div class="flex flex-col gap-1">
          <label class="text-xs font-medium text-gray-600">Level / Class *</label>
          <ClassroomSectionSelect v-model="enrollClassroomSectionId" size="small" placeholder="Select level" className="w-full" :showClassroomsOnly="isTertiary" :emitSectionId="true" />
        </div>
        <div v-if="isTertiary" class="flex flex-col gap-1">
          <label class="text-xs font-medium text-gray-600">Programme <span class="text-gray-400">(optional)</span></label>
          <ProgrammeSelect v-model="enrollProgrammeId" size="small" placeholder="Select programme" className="w-full" showClear />
        </div>
      </div>
      <template #footer>
        <Button label="Cancel" text severity="secondary" @click="showEnrollDialog = false" />
        <Button label="Enroll Students" icon="pi pi-calendar-plus" severity="help"
          :loading="enrolling" :disabled="!enrollCalendarId || !enrollClassroomSectionId"
          @click="handleEnroll" />
      </template>
    </Dialog>

    <!-- Reset Passwords Dialog: confirmation first, per-student summary after the call -->
    <Dialog v-model:visible="showResetPasswordsDialog"
        :header="resetPasswordsResult ? 'Password Reset Summary' : 'Reset Passwords'" modal class="w-full max-w-lg"
        :pt="{ root: { class: 'rounded-xl shadow-2xl overflow-hidden' }, header: { class: 'bg-gray-50 border-b' } }">
      <div class="p-4 space-y-4">
        <template v-if="!resetPasswordsResult">
          <div class="flex items-start gap-3 p-3 bg-amber-50 rounded-lg border border-amber-100" data-testid="reset-passwords-confirm">
            <i class="pi pi-key text-amber-500 text-lg mt-0.5"></i>
            <div>
              <p class="text-sm font-medium text-amber-800">
                You are about to reset the password of {{ selectedStudents.length }} {{ labels.STUDENT.toLowerCase() }}{{ selectedStudents.length > 1 ? 's' : '' }}.
              </p>
              <p class="text-xs text-amber-600 mt-1">
                Each password becomes the {{ labels.STUDENT.toLowerCase() }}'s surname in lowercase. Current passwords stop working immediately and this cannot be undone.
              </p>
            </div>
          </div>
          <ul class="max-h-48 overflow-y-auto divide-y divide-gray-100 rounded-lg border border-gray-100 text-sm" data-testid="reset-passwords-selection">
            <li v-for="s in selectedStudents" :key="String(s.id)" class="flex items-center justify-between gap-3 px-3 py-1.5">
              <span class="text-gray-800 truncate">{{ studentDisplayName(s) }}</span>
              <span class="text-xs text-gray-400 font-mono shrink-0">{{ s.matric_no }}</span>
            </li>
          </ul>
        </template>

        <template v-else>
          <div class="flex items-start gap-3 p-3 rounded-lg border"
            :class="resetPasswordsResult.failed_count === 0 ? 'bg-green-50 border-green-100' : 'bg-amber-50 border-amber-100'"
            data-testid="reset-passwords-summary">
            <i class="text-lg mt-0.5"
              :class="resetPasswordsResult.failed_count === 0 ? 'pi pi-check-circle text-green-500' : 'pi pi-exclamation-triangle text-amber-500'"></i>
            <div>
              <p class="text-sm font-medium" :class="resetPasswordsResult.failed_count === 0 ? 'text-green-800' : 'text-amber-800'">
                {{ resetPasswordsResult.message }}
              </p>
              <p class="text-xs mt-1" :class="resetPasswordsResult.failed_count === 0 ? 'text-green-600' : 'text-amber-600'">
                Reset {{ labels.STUDENT.toLowerCase() }}s now sign in with their surname in lowercase.
              </p>
            </div>
          </div>
          <div v-if="resetPasswordFailures.length > 0">
            <p class="text-xs font-semibold text-gray-600 mb-1">Could not be reset</p>
            <ul class="max-h-48 overflow-y-auto divide-y divide-gray-100 rounded-lg border border-red-100 text-sm" data-testid="reset-passwords-failures">
              <li v-for="row in resetPasswordFailures" :key="String(row.student_id)" class="px-3 py-1.5">
                <span class="text-gray-800">{{ row.student_name || resetPasswordRowName(row.student_id) }}</span>
                <span class="block text-xs text-red-600">{{ row.message }}</span>
              </li>
            </ul>
          </div>
        </template>
      </div>
      <template #footer>
        <template v-if="!resetPasswordsResult">
          <Button label="Cancel" text severity="secondary" @click="showResetPasswordsDialog = false" />
          <Button label="Reset Passwords" icon="pi pi-key" severity="warn"
            :loading="resettingPasswords" :disabled="selectedStudents.length === 0"
            @click="handleResetPasswords" data-testid="btn-confirm-reset-passwords" />
        </template>
        <Button v-else label="Done" @click="closeResetPasswordsDialog" data-testid="btn-close-reset-passwords" />
      </template>
    </Dialog>

  </div>
</template>

<script setup lang="ts">
import { useSchoolQuery } from '@/composables/queries/useSchoolQuery';
import { useSchoolLabels } from '@/composables/useSchoolLabels';
import { useStudents } from '@/composables/queries/useStudent';
import { loadOrRefetch } from '@/utils/lazyQuery';
import { useStudentMutations } from '@/composables/mutations/students/useStudentMutations';
import { useStudentOverviewMutations, type BulkResetStudentPasswordsResult } from '@/composables/mutations/students/useStudentOverviewMutations';
import { enrollmentStatusLabel, enrollmentStatusSeverity } from '@/utils/enrollmentStatus';
import { useUserStore } from '@/store/userStore';
import { useStudentOverviewStore } from '@/store/studentOverviewStore';
import { useToast } from 'primevue/usetoast';
import AcademicCalendarSelect from '@/components/common/AcademicCalendarSelect.vue';
import ClassroomSectionSelect from '@/components/common/ClassroomSectionSelect.vue';
import ProgrammeSelect from '@/components/common/ProgrammeSelect.vue';
import LoadErrorMessage from '@/components/common/LoadErrorMessage.vue';
import EnrollmentStatusSelect from '@/components/common/EnrollmentStatusSelect.vue';
import { storeToRefs } from 'pinia';
import StudentDetailsForm from '../AdmitStudent/StudentDetailsForm.vue';
import { useProgrammes } from '@/composables/queries/useProgrammes';
import { useCampuses } from '@/composables/queries/useCampuses';
import { useSchoolFeatures } from '@/composables/useSchoolFeatures';
import { getClassroomSectionLabel } from '@/utils/classroomLabel';
import { AppPermissions, can } from '@/utils/permissions';
import { StudentUpdateScope, type EnrollmentStatusEnum } from '@/types/composition-functions';

interface SelectOption {
  label: string;
  value: string;
}

const { labels } = useSchoolLabels();
const userStore = useUserStore();
const { deleteStudent: deleteStudentMutation } = useStudentMutations();
const {
  bulkUpdateStudents, bulkUpdating,
  disableStudents, disabling,
  enableStudents, enabling,
  updateStudentStatus, updatingStatus,
  enrollStudentsInCalendar, enrolling,
  deenrollStudents, deenrolling,
  bulkResetStudentPasswords, resettingPasswords,
} = useStudentOverviewMutations();

const studentOverviewStore = useStudentOverviewStore();
const { searchQuery, selectedClassroom, selectedProgramme, selectedCampus, selectedStatus, page, rows, hasSearched, cachedStudents, cachedTotalRecords } = storeToRefs(studentOverviewStore);

const selectedAcademicCalendar = ref<string | null>(null);
const selectedAccountStatus = ref<boolean | null>(null);
const accountStatusOptions = [
  { label: 'Active', value: true },
  { label: 'Disabled', value: false },
];

const toast = useToast();
const router = useRouter();

const showDeleteDialog = ref(false);
const showEditDialog = ref(false);
const showDisableDialog = ref(false);
const showEnableDialog = ref(false);
const showStatusDialog = ref(false);
const showBulkUpdateDialog = ref(false);
const showEnrollDialog = ref(false);
const enrollCalendarId = ref('');
const enrollClassroomSectionId = ref('');
const enrollProgrammeId = ref('');
const showDeenrollDialog = ref(false);
const deenrollCalendarId = ref('');
const showResetPasswordsDialog = ref(false);
const resetPasswordsResult = ref<BulkResetStudentPasswordsResult | null>(null);
const studentToDelete = ref<string | null>(null);
const editingStudent = ref<Record<string, unknown> | undefined>(undefined);
const deleting = ref(false);
const selectedStudents = ref<Record<string, unknown>[]>([]);
const ALL_VALUE = 'ALL';

// Disable/Enable form state
const disableReason = ref('');
const enableReason = ref('');

const isStudentActive = (s: Record<string, unknown>) => s.is_active !== false;

function studentDisplayName(s: Record<string, unknown> | undefined): string {
  if (!s) return `this ${labels.value.STUDENT.toLowerCase()}`;
  const name = [s.surname, s.first_name].map(v => String(v ?? '').trim()).filter(Boolean).join(' ');
  const matric = String(s.matric_no ?? '').trim();
  return name || matric || `this ${labels.value.STUDENT.toLowerCase()}`;
}

// Failed rows of the last bulk password reset, for the summary dialog
const resetPasswordFailures = computed(() => resetPasswordsResult.value?.results.filter(r => !r.success) ?? []);

// Selection partitioned by account status for the Disable dialog
const toDisable = computed(() => selectedStudents.value.filter(isStudentActive));
const alreadyDisabled = computed(() => selectedStudents.value.filter(s => !isStudentActive(s)));

// Selection partitioned by account status for the Enable dialog
const toEnable = computed(() => selectedStudents.value.filter(s => !isStudentActive(s)));
const alreadyEnabled = computed(() => selectedStudents.value.filter(isStudentActive));

// Status form state
const newStatus = ref<EnrollmentStatusEnum | null>(null);
const statusCalendarId = ref<string | null>(null);

// Bulk update form state
const bulkCalendarId = ref<string | null>(null);
const bulkProgrammeId = ref<string | null>(null);
const bulkClassroomSectionId = ref<string | null>(null);
const bulkCampusId = ref<string | null>(null);
const bulkMinUnits = ref<number | null>(null);
const bulkMaxUnits = ref<number | null>(null);
const bulkEntryLevel = ref<string | null>(null);
const bulkMinGraduationUnits = ref<number | null>(null);
const bulkUpdateScope = ref<StudentUpdateScope>(StudentUpdateScope.CurrentOnly);
const bulkCascade = ref(false);

const updateScopeOptions = [
  { label: 'Current Calendar Only', value: StudentUpdateScope.CurrentOnly },
  { label: 'All Calendars', value: StudentUpdateScope.AllCalendars },
];

const bulkHasChanges = computed(() => {
  const hasCalendar = selectedAcademicCalendar.value || bulkCalendarId.value;
  const hasField = bulkProgrammeId.value || bulkClassroomSectionId.value || bulkCampusId.value || bulkMinUnits.value !== null || bulkMaxUnits.value !== null || bulkEntryLevel.value || bulkMinGraduationUnits.value !== null;
  if (bulkMinUnits.value !== null && bulkMaxUnits.value !== null && bulkMinUnits.value > bulkMaxUnits.value) return false;
  return hasCalendar && hasField;
});

function confirmDelete(id: string) {
  studentToDelete.value = id;
  showDeleteDialog.value = true;
}

interface StudentAcademicCalendarShape {
  id?: unknown;
  programme?: { id?: unknown } | null;
  academicCalendar?: { id?: unknown } | null;
  initial_absent_days?: unknown;
  initial_late_days?: unknown;
  initial_present_days?: unknown;
  min_units_per_registration?: unknown;
  max_units_per_registration?: unknown;
  entry_level?: string | null;
  min_graduation_units?: number | null;
  status?: unknown;
}

interface CurrentClassroomSectionShape {
  id?: unknown;
}

function openEditDialog(student: Record<string, unknown>) {
  const sac = student.studentAcademicCalendar as StudentAcademicCalendarShape | null | undefined;
  const ccs = student.currentClassroomSection as CurrentClassroomSectionShape | null | undefined;
  editingStudent.value = {
    id: student.id,
    surname: student.surname,
    first_name: student.first_name,
    other_name: student.other_name,
    dob: student.dob,
    gender: student.gender,
    matric_no: student.matric_no,
    email: student.email,
    phone: student.phone,
    home_address: student.home_address,
    nationality: student.nationality,
    city: student.city,
    state: student.state,
    classroom_section_id: ccs?.id ?? null,
    programme_id: sac?.programme?.id ?? null,
    campus_id: null,
    student_academic_calendar: {
      id: sac?.id,
      academic_calendar_id: sac?.academicCalendar?.id,
      initial_absent_days: sac?.initial_absent_days,
      initial_late_days: sac?.initial_late_days,
      initial_present_days: sac?.initial_present_days,
      min_units_per_registration: sac?.min_units_per_registration,
      max_units_per_registration: sac?.max_units_per_registration,
      entry_level: sac?.entry_level ?? null,
      min_graduation_units: sac?.min_graduation_units ?? null,
      status: sac?.status,
    },
  };

  showEditDialog.value = true;
}

const { classrooms, school, loadingSchool } = useSchoolQuery();
const { programmes } = useProgrammes({ autoFetch: true, first: 200 });
const { campuses } = useCampuses({ autoFetch: true });
const { isTertiary, schoolType } = useSchoolFeatures();

const studentFilters = computed(() => ({
  page: page!.value,
  first: rows!.value,
  filter: {
    search: searchQuery!.value || undefined,
    classroom_section_ids: (selectedClassroom!.value && selectedClassroom!.value !== 'ALL') ? [selectedClassroom!.value] : undefined,
    programme_id: selectedProgramme!.value || undefined,
    campus_id: selectedCampus!.value || undefined,
    academic_calendar_id: selectedAcademicCalendar.value || undefined,
    status: selectedStatus!.value || undefined,
    is_active: selectedAccountStatus.value,
  },
  include_tertiary: isTertiary.value,
}));

const { students: studentsStore, refetchStudents, loadStudents } = useStudents({
  getStudentsOptions: { autoFetch: false },
  studentFilters: studentFilters,
});

const loadingStudents = studentsStore.loading;
const studentsError = studentsStore.error;

const students = computed(() => {
  if (studentsStore.data.value && studentsStore.data.value.length > 0) {
    return studentsStore.data.value;
  }
  return cachedStudents!.value || [];
});

const filteredStudents = computed(() => students.value);

const totalRecords = computed(() => {
  if (studentsStore.paginatorInfo.value) {
    return studentsStore.paginatorInfo.value.total;
  }
  return cachedTotalRecords!.value || 0;
});

watch(() => studentsStore.data.value, (newData) => {
  if (newData) {
    cachedStudents!.value = newData;
  }
});

watch(() => studentsStore.paginatorInfo.value, (newInfo) => {
  if (newInfo) {
    cachedTotalRecords!.value = newInfo.total;
  }
});

onMounted(() => {
  if (hasSearched!.value) {
    loadStudents();
  }
});

watch(selectedAcademicCalendar, (newVal) => {
  if (newVal && !hasSearched!.value) {
    hasSearched!.value = true;
    loadStudents();
  }
});

const levelOptions = computed<SelectOption[]>(() => {
  const options: SelectOption[] = [
    {
      label: 'All Levels',
      value: ALL_VALUE,
    },
  ];

  classrooms.value?.forEach((classroom) => {
    classroom?.classroomSections?.forEach((section) => {
      options.push({
        label: classroom.name,
        value: classroom?.classroomSections?.[0]?.id || '',
      });
    });
  });

  const unique = new Map<string, SelectOption>();
  options.forEach(o => unique.set(o.value, o));

  return Array.from(unique.values());
});

const classroomSectionOptions = computed<SelectOption[]>(() => {
  const options: SelectOption[] = [
    {
      label: `All ${labels.value.CLASS}s`,
      value: ALL_VALUE,
    },
  ];

  classrooms.value?.forEach((classroom) => {
    if (classroom?.classroomSections?.length) {
      classroom.classroomSections.forEach((cs) => {
        if (cs) {
          options.push({
            label: getClassroomSectionLabel(cs, classroom, schoolType.value),
            value: cs.id,
          });
        }
      });
    }
  });

  const unique = new Map<string, SelectOption>();
  options.forEach(o => unique.set(o.value, o));

  return Array.from(unique.values());
});

const programmeOptions = computed(() =>
  programmes.value?.map((p) => ({
    label: `${p.program_name}${p.degree ? ` — ${p.degree}` : ''}${p.type ? ` — ${p.type}` : ''}`,
    id: p.id,
  })) || [],
);

const campusOptions = computed(() => campuses.value || []);

// The list query re-runs by itself when its variables change (filters, page),
// so those handlers only need to start it. Search and Refresh must reach the
// server even when nothing changed, which load() alone never does after the
// first run.
const studentsQuery = { load: loadStudents, refetch: refetchStudents };

async function triggerSearch() {
  hasSearched!.value = true;
  page!.value = 1;
  // Let a page reset apply first, so the refetch asks for page 1.
  await nextTick();
  await loadOrRefetch(studentsQuery);
}

function onFilterChange() {
  hasSearched!.value = true;
  page!.value = 1;
  loadStudents();
}

function onPageChange(event: { page: number; first: number; rows: number }) {
  page!.value = event.page + 1;
  rows!.value = event.rows;
  loadStudents();
}

async function deleteStudent() {
  if (!studentToDelete.value) return;

  deleting.value = true;
  try {
    const success = await deleteStudentMutation(studentToDelete.value);
    if (success) {
      showDeleteDialog.value = false;
      await refetchStudents();
    }
  } catch (error: unknown) {
    // Error handling
  } finally {
    deleting.value = false;
  }
}

function viewStudent(id: string) {
  router.push({ name: 'StudentOverview', params: { id } });
}

// === Bulk Operation Handlers ===

function getSelectedIds(): string[] {
  return selectedStudents.value.map(s => String(s.id));
}

async function handleDisable() {
  const ids = toDisable.value.map(s => String(s.id));
  if (ids.length === 0) return;
  const success = await disableStudents({
    student_ids: ids,
    reason: disableReason.value,
    academic_calendar_id: selectedAcademicCalendar.value,
  });
  if (success) {
    showDisableDialog.value = false;
    disableReason.value = '';
    selectedStudents.value = [];
    await refetchStudents();
  }
}

async function handleEnable() {
  const ids = toEnable.value.map(s => String(s.id));
  if (ids.length === 0) return;
  const success = await enableStudents({
    student_ids: ids,
    reason: enableReason.value,
  });
  if (success) {
    showEnableDialog.value = false;
    enableReason.value = '';
    selectedStudents.value = [];
    await refetchStudents();
  }
}

async function handleStatusUpdate() {
  const calendarId = selectedAcademicCalendar.value || statusCalendarId.value;
  if (!newStatus.value || !calendarId) return;

  const success = await updateStudentStatus({
    student_ids: getSelectedIds(),
    status: newStatus.value,
    academic_calendar_id: calendarId,
  });
  if (success) {
    showStatusDialog.value = false;
    newStatus.value = null;
    statusCalendarId.value = null;
    selectedStudents.value = [];
    await refetchStudents();
  }
}

async function handleBulkUpdate() {
  const calendarId = selectedAcademicCalendar.value || bulkCalendarId.value;
  if (!calendarId) return;

  const success = await bulkUpdateStudents({
    student_ids: getSelectedIds(),
    academic_calendar_id: calendarId,
    programme_id: bulkProgrammeId.value,
    classroom_section_id: bulkClassroomSectionId.value !== ALL_VALUE ? bulkClassroomSectionId.value : null,
    campus_id: bulkCampusId.value,
    max_units_per_registration: bulkMaxUnits.value,
    min_units_per_registration: bulkMinUnits.value,
    entry_level: bulkEntryLevel.value || undefined,
    min_graduation_units: bulkMinGraduationUnits.value ?? undefined,
    update_scope: bulkUpdateScope.value,
    cascade_to_registrations: bulkCascade.value,
  });
  if (success) {
    showBulkUpdateDialog.value = false;
    bulkProgrammeId.value = null;
    bulkClassroomSectionId.value = null;
    bulkCampusId.value = null;
    bulkCalendarId.value = null;
    bulkMinUnits.value = null;
    bulkMaxUnits.value = null;
    bulkEntryLevel.value = null;
    bulkMinGraduationUnits.value = null;
    bulkUpdateScope.value = StudentUpdateScope.CurrentOnly;
    bulkCascade.value = false;
    selectedStudents.value = [];
    await refetchStudents();
  }
}

async function handleDeenroll() {
  if (!deenrollCalendarId.value) return;
  const success = await deenrollStudents({
    student_ids: selectedStudents.value.map(s => String(s.id)),
    academic_calendar_id: deenrollCalendarId.value,
  });
  if (success) {
    showDeenrollDialog.value = false;
    deenrollCalendarId.value = '';
    selectedStudents.value = [];
    await refetchStudents();
  }
}

async function handleEnroll() {
  if (!enrollCalendarId.value || !enrollClassroomSectionId.value) return;
  const success = await enrollStudentsInCalendar({
    student_ids: selectedStudents.value.map(s => String(s.id)),
    academic_calendar_id: enrollCalendarId.value,
    classroom_section_id: enrollClassroomSectionId.value,
    programme_id: enrollProgrammeId.value || undefined,
  });
  if (success) {
    showEnrollDialog.value = false;
    enrollCalendarId.value = '';
    enrollClassroomSectionId.value = '';
    enrollProgrammeId.value = '';
    selectedStudents.value = [];
    await refetchStudents();
  }
}

function openResetPasswordsDialog() {
  resetPasswordsResult.value = null;
  showResetPasswordsDialog.value = true;
}

// A failed row for an id that never resolved carries no name from the server;
// fall back to the selected row so the admin still sees who it was.
function resetPasswordRowName(studentId: string): string {
  return studentDisplayName(selectedStudents.value.find(s => String(s.id) === String(studentId)));
}

async function handleResetPasswords() {
  const ids = getSelectedIds();
  if (ids.length === 0) return;
  const result = await bulkResetStudentPasswords({ student_ids: ids });
  // Keep the confirmation (and the selection) when the request itself failed so the admin can retry.
  if (result) resetPasswordsResult.value = result;
}

function closeResetPasswordsDialog() {
  showResetPasswordsDialog.value = false;
  resetPasswordsResult.value = null;
  selectedStudents.value = [];
}

// === Avatar helpers ===
const AVATAR_COLOR_CLASSES = [
  '!bg-primary',
  '!bg-emerald-500',
  '!bg-purple-500',
  '!bg-amber-500',
  '!bg-rose-500',
  '!bg-cyan-600',
  '!bg-indigo-500',
  '!bg-pink-500',
  '!bg-teal-500',
  '!bg-orange-500',
];

function getStudentInitials(student: Record<string, unknown>): string {
  const surname = String(student.surname ?? '').trim();
  const firstName = String(student.first_name ?? '').trim();
  const initials = `${surname.charAt(0)}${firstName.charAt(0)}`.toUpperCase();
  return initials || '?';
}

function getAvatarColorClass(student: Record<string, unknown>): string {
  const seed = String(student.id ?? student.matric_no ?? student.surname ?? '');
  let hash = 0;
  for (let i = 0; i < seed.length; i++) {
    hash = (hash * 31 + seed.charCodeAt(i)) | 0;
  }
  const index = Math.abs(hash) % AVATAR_COLOR_CLASSES.length;
  return AVATAR_COLOR_CLASSES[index] ?? AVATAR_COLOR_CLASSES[0]!;
}

// === Filter chips ===
interface FilterChip {
  key: string;
  label: string;
  clear: () => void;
}

const academicCalendarLookup = computed<Record<string, string>>(() => {
  const map: Record<string, string> = {};
  const calendars = (school.value as { academicCalendars?: Array<{ id?: string; session?: { name?: string }; termStructure?: { name?: string } }> })?.academicCalendars ?? [];
  calendars.forEach((c) => {
    if (c?.id) {
      const sessionName = c.session?.name ?? '';
      const termName = c.termStructure?.name ?? '';
      map[c.id] = [sessionName, termName].filter(Boolean).join(' - ');
    }
  });
  return map;
});

const activeFilterChips = computed<FilterChip[]>(() => {
  const chips: FilterChip[] = [];

  if (searchQuery!.value) {
    chips.push({
      key: 'search',
      label: `Search: "${searchQuery!.value}"`,
      clear: () => { searchQuery!.value = ''; onFilterChange(); },
    });
  }

  if (selectedAcademicCalendar.value) {
    const label = academicCalendarLookup.value[selectedAcademicCalendar.value] || 'Session';
    chips.push({
      key: 'session',
      label: `Session: ${label}`,
      clear: () => { selectedAcademicCalendar.value = null; onFilterChange(); },
    });
  }

  if (selectedClassroom!.value && selectedClassroom!.value !== ALL_VALUE) {
    const opts = isTertiary.value ? levelOptions.value : classroomSectionOptions.value;
    const label = opts.find(o => o.value === selectedClassroom!.value)?.label ?? labels.value.CLASS;
    chips.push({
      key: 'classroom',
      label: `${labels.value.CLASS}: ${label}`,
      clear: () => { selectedClassroom!.value = null; onFilterChange(); },
    });
  }

  if (selectedStatus!.value) {
    chips.push({
      key: 'status',
      label: `Status: ${enrollmentStatusLabel(selectedStatus!.value)}`,
      clear: () => { selectedStatus!.value = null; onFilterChange(); },
    });
  }

  if (isTertiary.value && selectedProgramme!.value) {
    const label = programmeOptions.value.find((p: { id: string; label: string }) => p.id === selectedProgramme!.value)?.label ?? 'Programme';
    chips.push({
      key: 'programme',
      label: `Programme: ${label}`,
      clear: () => { selectedProgramme!.value = null; onFilterChange(); },
    });
  }

  if (isTertiary.value && selectedCampus!.value) {
    const campus = campusOptions.value.find(c => String(c?.id) === String(selectedCampus!.value));
    chips.push({
      key: 'campus',
      label: `Campus: ${campus?.name ?? 'Campus'}`,
      clear: () => { selectedCampus!.value = null; onFilterChange(); },
    });
  }

  if (selectedAccountStatus.value !== null && selectedAccountStatus.value !== undefined) {
    chips.push({
      key: 'account',
      label: `Account: ${selectedAccountStatus.value ? 'Active' : 'Disabled'}`,
      clear: () => { selectedAccountStatus.value = null; onFilterChange(); },
    });
  }

  return chips;
});

function clearAllFilters() {
  searchQuery!.value = '';
  selectedAcademicCalendar.value = null;
  selectedClassroom!.value = null;
  selectedStatus!.value = null;
  selectedAccountStatus.value = null;
  if (isTertiary.value) {
    selectedProgramme!.value = null;
    selectedCampus!.value = null;
  }
  onFilterChange();
}

async function refreshList() {
  await loadOrRefetch(studentsQuery);
}
</script>

<style lang="scss" scoped>
:deep(.modern-table) {
  .p-datatable-header {
    background: transparent;
    border: none;
    padding: 0;
  }

  .p-datatable-thead > tr > th {
    background: #f9fafb;
    color: #4b5563;
    font-weight: 600;
    font-size: 0.7rem;
    text-transform: uppercase;
    letter-spacing: 0.04em;
    padding: 0.5rem 1rem;
    border-bottom: 1px solid #e5e7eb;
  }

  .p-datatable-tbody > tr {
    transition: background-color 0.2s;

    > td {
      padding: 0.5rem 1rem;
      border-bottom: 1px solid #f3f4f6;
      font-size: 0.8125rem;
    }

    &:hover {
      background-color: #f9fafb;
    }
  }

  .p-paginator {
    border-top: 1px solid #e5e7eb;
    padding: 0.5rem 0.75rem;
    background: transparent;
    font-size: 0.8125rem;

    .p-paginator-page.p-highlight {
      background: #eff6ff;
      color: #3b82f6;
    }
  }
}

.slide-down-enter-active,
.slide-down-leave-active {
  transition: all 0.2s ease;
}

.slide-down-enter-from,
.slide-down-leave-to {
  opacity: 0;
  transform: translateY(-8px);
}
</style>
